import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Callout,
  CheckIcon,
  Label,
  LoadingSpinner,
  LockIcon,
  TextInput,
} from '../components/ui';
import { useAuth } from '../hooks/useAuth';
import { t } from '../i18n';
import { ApiError, api, type InviteInfo } from '../services/api';

export function InviteAcceptPage() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { establishSession } = useAuth();
  const labels = t().invite;
  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getInvite(token);
        if (!cancelled) setInfo(data);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof ApiError ? err.message : labels.invalidInvite,
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [token, labels.invalidInvite]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError(labels.passwordMin);
      return;
    }
    if (password !== confirm) {
      setError(labels.passwordMismatch);
      return;
    }

    setSaving(true);
    try {
      const result = await api.acceptInvite(token, password);
      establishSession(result.accessToken, result.user);
      if (result.user.role === 'SUPER_ADMIN') {
        navigate('/platform/tenants', { replace: true });
      } else {
        navigate(
          result.user.tenantSetupCompleted ? '/' : '/setup/company',
          { replace: true },
        );
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface p-4 text-ink">
      <div className="w-full max-w-md rounded-xl border border-line bg-panel p-6 shadow-bf-sm sm:p-8">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-500/15 text-teal-700 dark:text-teal-300">
            <LockIcon className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold">
              {labels.title}
            </h1>
            <p className="text-sm text-muted">{labels.hint}</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <LoadingSpinner />
          </div>
        ) : error && !info ? (
          <div className="space-y-4">
            <Callout variant="danger">{error}</Callout>
            <Link
              to="/login"
              className="inline-block text-sm font-medium text-teal-700 hover:underline dark:text-teal-300"
            >
              {labels.backToLogin}
            </Link>
          </div>
        ) : info ? (
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="rounded-lg border border-line bg-surface/50 px-3 py-3 text-sm">
              <p>
                <span className="text-muted">
                  {info.tenantName ? labels.office : labels.platformOffice}:{' '}
                </span>
                <span className="font-medium">
                  {info.tenantName ?? t().app.name}
                </span>
              </p>
              <p className="mt-1">
                <span className="text-muted">{labels.email}: </span>
                <span className="font-medium">{info.email}</span>
              </p>
            </div>

            {error ? <Callout variant="danger">{error}</Callout> : null}

            <div>
              <Label htmlFor="invite-password">{labels.password}</Label>
              <TextInput
                id="invite-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="invite-confirm">{labels.confirmPassword}</Label>
              <TextInput
                id="invite-confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
              />
            </div>

            <Button type="submit" className="w-full" loading={saving}>
              <CheckIcon className="h-4 w-4" />
              {labels.submit}
            </Button>
          </form>
        ) : null}
      </div>
    </div>
  );
}
